import { ChangeEvent } from "react";
import { useAtomValue } from "jotai";
import { Box, Button, Stack, Typography } from "@mui/material";

import { PHENOMENON_GROUP_IDS, UNIT_IDS } from "utils/constsLtool";
import { mappingAtom } from "atoms/mappingAtoms";
import { getMappingLabel } from "utils/mappingLabel";

export type UnitId = (typeof UNIT_IDS)[number];
export type PhenomenonGroupId = (typeof PHENOMENON_GROUP_IDS)[number];

export type AllCopyFilesMap = Record<UnitId, Record<PhenomenonGroupId, File | null>>;
export type UnitCopyFilesMap = Record<PhenomenonGroupId, File | null>;

export const createEmptyAllCopyFiles = (): AllCopyFilesMap =>
    UNIT_IDS.reduce((acc, unit) => {
        acc[unit] = PHENOMENON_GROUP_IDS.reduce((inner, phenomenon) => {
            inner[phenomenon] = null;
            return inner;
        }, {} as Record<PhenomenonGroupId, File | null>);
        return acc;
    }, {} as AllCopyFilesMap);

export const areAllFilesSelected = (
    files: AllCopyFilesMap,
    groupIds: readonly PhenomenonGroupId[] = PHENOMENON_GROUP_IDS,
) =>
    UNIT_IDS.every((unit) =>
        groupIds.every((phenomenon) => Boolean(files[unit][phenomenon])),
    );

// groupIds は HEV で gn/vib のみに絞るため可変（既定は BEV の vib/gn/aco）
export const createEmptyPhenomenonFiles = (
    groupIds: readonly PhenomenonGroupId[] = PHENOMENON_GROUP_IDS,
): UnitCopyFilesMap =>
    groupIds.reduce((acc, phenomenon) => {
        acc[phenomenon] = null;
        return acc;
    }, {} as UnitCopyFilesMap);

const extractFileFromEvent = (event: ChangeEvent<HTMLInputElement>): File | null => {
    const input = event.target;
    const file = input.files?.[0] ?? null;
    input.value = "";
    return file;
};

type AllCopyFileSelectorProps = {
    files: AllCopyFilesMap;
    onChange: (unit: UnitId, phenomenon: PhenomenonGroupId, file: File | null) => void;
    groupIds?: readonly PhenomenonGroupId[];
};

export const AllCopyFileSelector = ({
    files,
    onChange,
    groupIds = PHENOMENON_GROUP_IDS,
}: AllCopyFileSelectorProps) => {
    const mapping = useAtomValue(mappingAtom);
    return (
    <Stack spacing={2} sx={{ mt: 2 }}>
        {UNIT_IDS.map((unit) => (
            <Box
                key={unit}
                sx={{
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 1,
                    p: 2,
                }}
            >
                <Typography variant="subtitle1" sx={{ mb: 1 }}>
                    Unit ID: {getMappingLabel(mapping, 'unit_id', unit)}
                </Typography>
                <Stack spacing={1}>
                    {groupIds.map((phenomenon) => {
                        const file = files[unit][phenomenon];
                        return (
                            <Stack
                                key={`${unit}-${phenomenon}`}
                                direction="row"
                                spacing={2}
                                alignItems="center"
                            >
                                <Button variant="outlined" component="label">
                                    {`Excelを選択 (${getMappingLabel(mapping, 'condition_group_id', phenomenon)})`}
                                    <input
                                        hidden
                                        type="file"
                                        accept=".xlsx,.xls"
                                        onChange={(event) =>
                                            onChange(unit, phenomenon, extractFileFromEvent(event))
                                        }
                                    />
                                </Button>
                                {file && (
                                    <Typography variant="body2" sx={{ color: "text.secondary" }}>
                                        選択中: {file.name}
                                    </Typography>
                                )}
                            </Stack>
                        );
                    })}
                </Stack>
            </Box>
        ))}
    </Stack>
    );
};

type UnitCopyFileSelectorProps = {
    files: UnitCopyFilesMap;
    unitSelected: boolean;
    onChange: (phenomenon: PhenomenonGroupId, file: File | null) => void;
    groupIds?: readonly PhenomenonGroupId[];
};

export const UnitCopyFileSelector = ({
    files,
    unitSelected,
    onChange,
    groupIds = PHENOMENON_GROUP_IDS,
}: UnitCopyFileSelectorProps) => {
    const mapping = useAtomValue(mappingAtom);
    return (
    <Stack spacing={1.5} sx={{ mt: 2 }}>
        {groupIds.map((phenomenon) => {
            const file = files[phenomenon];
            return (
                <Stack
                    key={`unit-${phenomenon}`}
                    direction="row"
                    spacing={2}
                    alignItems="center"
                >
                    <Button variant="outlined" component="label" disabled={!unitSelected}>
                        {`Excelを選択 (${getMappingLabel(mapping, 'condition_group_id', phenomenon)})`}
                        <input
                            hidden
                            type="file"
                            accept=".xlsx,.xls"
                            onChange={(event) => onChange(phenomenon, extractFileFromEvent(event))}
                        />
                    </Button>
                    {file && (
                        <Typography variant="body2" sx={{ color: "text.secondary" }}>
                            選択中: {file.name}
                        </Typography>
                    )}
                </Stack>
            );
        })}
        {!unitSelected && (
            <Typography variant="caption" color="text.secondary">
                Unit IDを先に選択してください。
            </Typography>
        )}
    </Stack>
    );
};

type SingleFileSelectorProps = {
    file: File | null;
    onChange: (file: File | null) => void;
};

export const SingleFileSelector = ({ file, onChange }: SingleFileSelectorProps) => (
    <Stack direction="row" spacing={2} sx={{ mt: 2 }} alignItems="center">
        <Button variant="outlined" component="label">
            Excelを選択
            <input
                hidden
                type="file"
                accept=".xlsx,.xls"
                onChange={(event) => onChange(extractFileFromEvent(event))}
            />
        </Button>
        {file && (
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
                選択中: {file.name}
            </Typography>
        )}
    </Stack>
);
